import { Link, useNavigate } from 'react-router-dom';
import { Plus, LayoutGrid, LogOut, PanelLeft, PanelLeftClose, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { firestore } from '@/lib/firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { useQuery } from '@tanstack/react-query';
import { ConditionalWrapper } from './ConditionalWrapper';
import { Conversation, Content } from '@shared/types';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface SidebarProps {
  isSidebarOpen: boolean;
  onToggle?: () => void;
  variant?: 'desktop' | 'mobile';
  onClose?: () => void;
}

export function Sidebar({
  isSidebarOpen,
  onToggle,
  variant = 'desktop',
  onClose,
}: SidebarProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useTranslation('common');
  const isMobile = variant === 'mobile';

  const { data: recentConversations } = useQuery<Conversation[]>({
    queryKey: ['conversations', 'recent'],
    initialData: [],
    enabled: !!user?.id,
    queryFn: async () => {
      const convsCol = collection(firestore, 'conversations');
      const q = query(
        convsCol,
        where('user_id', '==', user?.id ?? ''),
        orderBy('updated_at', 'desc'),
        limit(10),
      );
      const snap = await getDocs(q);
      const conversations = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Conversation[];

      const conversationsWithTitles = await Promise.all(
        conversations.map(async (conv) => {
          if (
            conv.title &&
            conv.title.toLowerCase() !== 'new conversation' &&
            conv.title.toLowerCase() !== '新しい会話' &&
            conv.title.toLowerCase() !== 'untitled' &&
            conv.title.toLowerCase() !== '無題' &&
            conv.title.toLowerCase() !== 'conversation' &&
            conv.title.toLowerCase() !== '会話'
          ) {
            return conv;
          }

          const msgsCol = collection(firestore, 'conversations', conv.id, 'messages');
          const mq = query(msgsCol, orderBy('created_at', 'asc'), limit(1));
          const msgSnap = await getDocs(mq);

          if (!msgSnap.empty) {
            const firstMsg = msgSnap.docs[0].data();
            const content = firstMsg.content as Content;
            const text = content?.text || '';
            const preview = text.substring(0, 40).trim();
            return {
              ...conv,
              title: preview || conv.title || t('untitledProject'),
            };
          }

          return conv;
        }),
      );

      return conversationsWithTitles;
    },
  });

  const handleNav = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const widthClass = isMobile
    ? 'w-full'
    : isSidebarOpen
      ? 'w-60'
      : 'w-14';

  // Bright teal sidebar with indigo text & interaction states
  return (
    <div
      className={`${widthClass} relative flex h-full flex-shrink-0 flex-col border-r-2 border-brand-indigo/20 bg-brand-teal pb-2 transition-all duration-200`}
    >
      {/* Logo / mobile close */}
      <div className="flex items-center justify-between px-3 pb-2 pt-3">
        <ConditionalWrapper
          condition={!isSidebarOpen && !isMobile}
          wrapper={(children) => (
            <Tooltip>
              <TooltipTrigger asChild>{children}</TooltipTrigger>
              <TooltipContent side="right">
                <span className="font-medium">{t('home')}</span>
              </TooltipContent>
            </Tooltip>
          )}
        >
          <Link to="/home" onClick={onClose}>
            <div className="flex cursor-pointer items-center gap-2 px-1">
              <Logo className="h-7 w-7 min-w-7 text-brand-indigo" />
              {(isSidebarOpen || isMobile) && (
                <span className="font-display text-base font-extrabold tracking-tight text-brand-indigo">
                  AgentCAD
                </span>
              )}
            </div>
          </Link>
        </ConditionalWrapper>

        {isMobile && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-brand-indigo transition-colors hover:bg-brand-teal-soft active:bg-brand-teal-soft"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className={`${(isSidebarOpen || isMobile) ? 'px-3' : 'px-2'} hide-scrollbar flex-1 overflow-y-auto py-2 transition-all duration-200`}
        >
          {/* New project — solid indigo so it pops on the teal bg */}
          <ConditionalWrapper
            condition={!isSidebarOpen && !isMobile}
            wrapper={(children) => (
              <Tooltip>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent side="right">
                  <span className="font-medium">{t('newProject')}</span>
                </TooltipContent>
              </Tooltip>
            )}
          >
            <button
              type="button"
              onClick={() => handleNav('/home')}
              className={`mb-3 flex items-center gap-2 rounded-lg border-2 border-brand-indigo bg-brand-indigo text-brand-cream transition-colors hover:bg-brand-indigo-hover ${
                (isSidebarOpen || isMobile)
                  ? 'h-10 w-full justify-start px-3 text-sm font-bold'
                  : 'h-10 w-10 justify-center'
              }`}
            >
              <Plus className="h-4 w-4 min-w-4" strokeWidth={2.5} />
              {(isSidebarOpen || isMobile) && <span>{t('newProject')}</span>}
            </button>
          </ConditionalWrapper>

          {/* Navigation */}
          <nav>
            <ConditionalWrapper
              condition={!isSidebarOpen && !isMobile}
              wrapper={(children) => (
                <Tooltip>
                  <TooltipTrigger asChild>{children}</TooltipTrigger>
                  <TooltipContent side="right">
                    <span className="font-medium">{t('projects')}</span>
                  </TooltipContent>
                </Tooltip>
              )}
            >
              <Link to="/history" onClick={onClose}>
                <div
                  className={`flex items-center gap-2 rounded-lg font-semibold text-brand-indigo transition-colors hover:bg-brand-teal-soft ${
                    (isSidebarOpen || isMobile)
                      ? 'h-10 w-full px-3'
                      : 'h-10 w-10 justify-center'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4 min-w-4" strokeWidth={2.25} />
                  {(isSidebarOpen || isMobile) && (
                    <span className="text-sm">{t('projects')}</span>
                  )}
                </div>
              </Link>
            </ConditionalWrapper>

            {/* Recent conversations */}
            {(isSidebarOpen || isMobile) && recentConversations && recentConversations.length > 0 && (
              <div className="mt-4 ml-3 flex flex-col gap-0.5 border-l-2 border-brand-indigo/40 pl-3">
                <span className="mb-1 block px-2 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-indigo/70">
                  {t('projects')}
                </span>
                {recentConversations.map((conversation: Omit<Conversation, 'message_count' | 'last_message_at'>) => (
                  <Link
                    to={`/editor/${conversation.id}`}
                    key={conversation.id}
                    onClick={onClose}
                  >
                    <span className="line-clamp-1 block rounded-md px-2 py-1.5 text-xs font-medium text-brand-indigo transition-colors duration-150 hover:bg-brand-indigo hover:text-brand-cream">
                      {conversation.title}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </nav>
        </div>

        {/* Toggle + Language + Logout */}
        <div
          className={`${(isSidebarOpen || isMobile) ? 'px-3' : 'px-2'} pb-1 pt-2 border-t-2 border-brand-indigo/20`}
        >
          {/* Sidebar toggle (desktop only) */}
          {onToggle && !isMobile && (
            <ConditionalWrapper
              condition={!isSidebarOpen}
              wrapper={(children) => (
                <Tooltip>
                  <TooltipTrigger asChild>{children}</TooltipTrigger>
                  <TooltipContent side="right">
                    <span className="font-medium">{t('expandSidebar')}</span>
                  </TooltipContent>
                </Tooltip>
              )}
            >
              <Button
                variant="ghost"
                className={`mb-1 font-semibold text-brand-indigo hover:bg-brand-teal-soft hover:text-brand-indigo ${
                  isSidebarOpen
                    ? 'flex w-full items-center justify-start gap-2 rounded-lg px-3 py-2'
                    : 'flex h-10 w-10 items-center justify-center rounded-lg p-0'
                }`}
                onClick={onToggle}
              >
                {isSidebarOpen ? (
                  <PanelLeftClose className="h-4 w-4 min-w-4" />
                ) : (
                  <PanelLeft className="h-4 w-4 min-w-4" />
                )}
                {isSidebarOpen && <span className="text-sm font-medium">{t('collapse')}</span>}
              </Button>
            </ConditionalWrapper>
          )}
          <LanguageSwitcher compact={!isSidebarOpen && !isMobile} />
        </div>
        <div className={`${(isSidebarOpen || isMobile) ? 'px-3' : 'px-2'} pb-2`}>
          <ConditionalWrapper
            condition={!isSidebarOpen && !isMobile}
            wrapper={(children) => (
              <Tooltip>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent side="right">
                  <span className="font-medium">{t('logout')}</span>
                </TooltipContent>
              </Tooltip>
            )}
          >
            <Button
              variant="ghost"
              className={`font-semibold text-brand-indigo hover:bg-brand-teal-soft hover:text-brand-indigo ${
                (isSidebarOpen || isMobile)
                  ? 'flex w-full items-center justify-start gap-2 rounded-lg px-3 py-2'
                  : 'flex h-10 w-10 items-center justify-center rounded-lg p-0'
              }`}
              onClick={() => {
                signOut();
                onClose?.();
              }}
            >
              <LogOut className="h-4 w-4 min-w-4" />
              {(isSidebarOpen || isMobile) && (
                <span className="text-sm font-medium">{t('logout')}</span>
              )}
            </Button>
          </ConditionalWrapper>
        </div>
      </div>
    </div>
  );
}
